import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const SalaryClone = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { sourceEmployee, sourceSalaryDetails } = location.state || {};

    const [employees, setEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [processing, setProcessing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('Active');

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    // Fetch all employees except the source employee
    const fetchEmployees = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE}/get_employees.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 10000
            });

            if (response.data && response.data.success) {
                // Filter out the source employee
                const filteredEmployees = response.data.employees.filter(
                    emp => emp.emp_id !== sourceEmployee?.emp_id
                );
                setEmployees(filteredEmployees);
            } else {
                setEmployees([]);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
            setError('Failed to fetch employees');
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    }, [API_BASE, sourceEmployee]);

    useEffect(() => {
        if (!sourceEmployee || !sourceSalaryDetails) {
            navigate('/salary-details');
            return;
        }
        fetchEmployees();
    }, [sourceEmployee, sourceSalaryDetails, navigate, fetchEmployees]);

    // Handle employee selection
    const toggleEmployeeSelection = (empId) => {
        if (selectedEmployees.includes(empId)) {
            setSelectedEmployees(selectedEmployees.filter(id => id !== empId));
        } else {
            setSelectedEmployees([...selectedEmployees, empId]);
        }
    };

    // Select all filtered employees
    const selectAll = () => {
        const filtered = filteredEmployees.map(emp => emp.emp_id);
        setSelectedEmployees(filtered);
    };

    // Deselect all
    const deselectAll = () => {
        setSelectedEmployees([]);
    };

    // Clone salary details
    const handleClone = async () => {
        if (selectedEmployees.length === 0) {
            setError('Please select at least one employee to clone salary to');
            return;
        }

        if (!window.confirm(`Are you sure you want to clone salary components from ${sourceEmployee.first_name} ${sourceEmployee.last_name} to ${selectedEmployees.length} employee(s)?`)) {
            return;
        }

        setProcessing(true);
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}/clone_salary.php`, {
                source_emp_id: sourceEmployee.emp_id,
                target_emp_ids: selectedEmployees,
                salary_details: sourceSalaryDetails
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setSuccess(`Successfully cloned salary components to ${response.data.affected_employees || selectedEmployees.length} employee(s)`);
                setSelectedEmployees([]);
            } else {
                setError(response.data.error || 'Failed to clone salary');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Failed to clone salary components';
            setError(errorMsg);
            console.error('Error cloning salary:', error);
        } finally {
            setProcessing(false);
        }
    };

    // Filter employees
    const filteredEmployees = employees.filter(employee => {
        const matchesSearch =
            employee.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.emp_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.email?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = filterStatus === 'All' || employee.status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    if (!sourceEmployee) {
        return (
            <div className="container-fluid py-4">
                <div className="alert alert-warning">
                    No source employee selected. Redirecting...
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <button
                                            className="btn btn-outline-secondary btn-sm me-3"
                                            onClick={() => navigate('/salary-details')}
                                            title="Back to Salary Details"
                                        >
                                            <i className="bi bi-arrow-left"></i>
                                        </button>
                                        <div>
                                            <h2 className="h4 mb-1 fw-bold text-dark">
                                                <i className="bi bi-copy me-2 text-success"></i>
                                                Clone Salary Components
                                            </h2>
                                            <p className="text-muted mb-0">
                                                Copy salary components from one employee to others
                                            </p>
                                            <small className="text-muted">
                                                Source: {sourceEmployee.first_name} {sourceEmployee.last_name}
                                                ({sourceEmployee.emp_code}) - {sourceSalaryDetails?.length || 0} components
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <div className="btn-group">
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={() => navigate('/salary-details')}
                                        >
                                            <i className="bi bi-arrow-left me-2"></i>
                                            Back to Salary Details
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Source Employee Card */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-primary">
                        <div className="card-header bg-primary text-white">
                            <i className="bi bi-person-check me-2"></i>
                            Source Employee (Salary Components Source)
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-3">
                                    <div className="d-flex align-items-center">
                                        <div className="employee-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                            style={{ width: '60px', height: '60px' }}
                                        >
                                            <i className="bi bi-person fs-4"></i>
                                        </div>
                                        <div>
                                            <h5 className="mb-1">{sourceEmployee.first_name} {sourceEmployee.last_name}</h5>
                                            <p className="text-muted mb-0">{sourceEmployee.emp_code}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="mb-2">
                                        <small className="text-muted">Email</small>
                                        <div>{sourceEmployee.email || 'N/A'}</div>
                                    </div>
                                    <div>
                                        <small className="text-muted">Status</small>
                                        <div>
                                            <span className={`badge ${sourceEmployee.status === 'Active' ? 'bg-success' :
                                                    sourceEmployee.status === 'Inactive' ? 'bg-secondary' :
                                                        'bg-danger'
                                                }`}>
                                                {sourceEmployee.status}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-2">
                                        <small className="text-muted">Salary Components ({sourceSalaryDetails?.length || 0})</small>
                                        <div className="mt-1">
                                            {sourceSalaryDetails && sourceSalaryDetails.length > 0 ? (
                                                <div className="d-flex flex-wrap gap-1">
                                                    {sourceSalaryDetails.slice(0, 5).map((detail, index) => (
                                                        <span key={index} className="badge bg-info">
                                                            {detail.component_type === 'Earning' || detail.component_type === 'Earing' ? '+' : '-'}
                                                            {detail.component_name || `Component ${index + 1}`}
                                                        </span>
                                                    ))}
                                                    {sourceSalaryDetails.length > 5 && (
                                                        <span className="badge bg-secondary">
                                                            +{sourceSalaryDetails.length - 5} more
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-muted">No salary components</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Messages */}
            {error && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>
                                <strong>Error:</strong> {error}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {success && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-success d-flex align-items-center" role="alert">
                            <i className="bi bi-check-circle-fill me-2"></i>
                            <div>
                                <strong>Success:</strong> {success}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Target Employees Selection */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">
                                    <i className="bi bi-people me-2"></i>
                                    Select Target Employees ({filteredEmployees.length} available)
                                </h5>
                                <div className="btn-group">
                                    <button
                                        className="btn btn-outline-success btn-sm"
                                        onClick={selectAll}
                                        disabled={filteredEmployees.length === 0}
                                    >
                                        <i className="bi bi-check-all me-1"></i>
                                        Select All
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={deselectAll}
                                    >
                                        <i className="bi bi-x-circle me-1"></i>
                                        Deselect All
                                    </button>
                                </div>
                            </div>

                            {/* Filters */}
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Search employees..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <button
                                                className="btn btn-outline-secondary border-start-0"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select"
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                    >
                                        <option value="All">All Status</option>
                                        <option value="Active">Active Only</option>
                                        <option value="Inactive">Inactive Only</option>
                                        <option value="Resigned">Resigned Only</option>
                                    </select>
                                </div>
                                <div className="col-md-3 text-end">
                                    <div className="badge bg-primary p-2">
                                        Selected: {selectedEmployees.length}
                                    </div>
                                </div>
                            </div>

                            {/* Employees List */}
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th style={{ width: '50px' }}></th>
                                            <th>Employee</th>
                                            <th>Contact</th>
                                            <th>Status</th>
                                            <th>Current Salary Components</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="5" className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <p className="mt-2 text-muted">Loading employees...</p>
                                                </td>
                                            </tr>
                                        ) : filteredEmployees.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" className="text-center py-4">
                                                    <i className="bi bi-people display-1 text-muted"></i>
                                                    <h5 className="mt-3 text-muted">No employees found</h5>
                                                    <p className="text-muted">
                                                        {searchTerm || filterStatus !== 'All'
                                                            ? "No employees match your search criteria."
                                                            : "No other employees available."
                                                        }
                                                    </p>
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredEmployees.map(employee => {
                                                const hasExistingComponents = sourceSalaryDetails?.some(
                                                    detail => detail.emp_id === employee.emp_id
                                                );

                                                return (
                                                    <tr
                                                        key={employee.emp_id}
                                                        className={selectedEmployees.includes(employee.emp_id) ? 'table-success' : ''}
                                                    >
                                                        <td>
                                                            <div className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    checked={selectedEmployees.includes(employee.emp_id)}
                                                                    onChange={() => toggleEmployeeSelection(employee.emp_id)}
                                                                    id={`emp-${employee.emp_id}`}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="employee-icon bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                                                    style={{ width: '40px', height: '40px' }}
                                                                >
                                                                    <i className="bi bi-person"></i>
                                                                </div>
                                                                <div>
                                                                    <div className="fw-semibold">
                                                                        {employee.first_name} {employee.last_name}
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        ID: {employee.emp_id} | Code: {employee.emp_code}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <small className="text-muted d-block">{employee.email || 'No email'}</small>
                                                            <small className="text-muted">{employee.phone || 'No phone'}</small>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${employee.status === 'Active' ? 'bg-success' :
                                                                    employee.status === 'Inactive' ? 'bg-secondary' :
                                                                        'bg-danger'
                                                                }`}>
                                                                {employee.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {hasExistingComponents ? (
                                                                <span className="badge bg-warning">
                                                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                                                    Has existing components
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-secondary">
                                                                    No salary components
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Action Buttons */}
                            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                                <div>
                                    <small className="text-muted">
                                        Selected: {selectedEmployees.length} employee(s)
                                    </small>
                                </div>
                                <div className="btn-group">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => navigate('/salary-details')}
                                        disabled={processing}
                                    >
                                        <i className="bi bi-x-circle me-2"></i>
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-success"
                                        onClick={handleClone}
                                        disabled={selectedEmployees.length === 0 || processing}
                                    >
                                        {processing ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Cloning...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-copy me-2"></i>
                                                Clone Salary to Selected ({selectedEmployees.length})
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Information Card */}
            <div className="row">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body">
                            <h6 className="fw-bold mb-3">
                                <i className="bi bi-info-circle me-2 text-info"></i>
                                About Salary Cloning
                            </h6>
                            <ul className="mb-0">
                                <li>This will copy all salary components from the source employee to selected employees</li>
                                <li>If target employee already has salary components, they will be replaced</li>
                                <li>The cloned components will have the same values and calculation types</li>
                                <li>Only employees without salary templates will be affected</li>
                                <li>You can preview the source components in the top card</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
        .employee-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        tr.table-success:hover {
          background-color: rgba(25, 135, 84, 0.1);
        }
      `}</style>
        </div>
    );
};

export default SalaryClone;